import React, { useState, useEffect } from 'react';
import { Mail, Smartphone, Monitor, RefreshCw, Save, AlertCircle, CheckCircle } from 'lucide-react';
import { changePreferencesApi } from '../../services/changePreferences.api';
import { UserChangePreferenceState, NotificationType } from '../../types/changeTracking';
import { notify } from '../../core/notifications/notify';

interface ChangeNotificationPreferencesProps {
  onSave?: () => void;
}

const ChangeNotificationPreferences: React.FC<ChangeNotificationPreferencesProps> = ({ onSave }) => {
  const [preferences, setPreferences] = useState<UserChangePreferenceState[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState<string>('');

  // Available options
  const entityTypes = [
    { value: 'user', label: 'User Changes', description: 'Account and user profile updates' },
    { value: 'company', label: 'Company Changes', description: 'Company information and settings' },
    { value: 'framework', label: 'Compliance Frameworks', description: 'Framework creation and modifications' },
    { value: 'permission', label: 'Permissions', description: 'Access control and permissions' },
    { value: 'document', label: 'Documents', description: 'Document uploads and changes' },
    { value: 'compliance_check', label: 'Compliance Checks', description: 'Compliance validation results' }
  ];

  const changeTypes = [
    { value: 'create', label: 'Creations', description: 'New items created' },
    { value: 'update', label: 'Updates', description: 'Existing items modified' },
    { value: 'delete', label: 'Deletions', description: 'Items removed' }
  ];

  const severityLevels = [
    { value: 'low', label: 'Low', description: 'Minor changes', color: 'text-blue-600' },
    { value: 'medium', label: 'Medium', description: 'Moderate changes', color: 'text-yellow-600' },
    { value: 'high', label: 'High', description: 'Important changes', color: 'text-orange-600' },
    { value: 'critical', label: 'Critical', description: 'Urgent changes', color: 'text-red-600' }
  ];

  const notificationChannels = [
    { type: 'email' as NotificationType, label: 'Email', icon: Mail, color: 'text-blue-600', bgColor: 'bg-blue-50' },
    { type: 'in_app' as NotificationType, label: 'In-App', icon: Monitor, color: 'text-green-600', bgColor: 'bg-green-50' },
    { type: 'push' as NotificationType, label: 'Push', icon: Smartphone, color: 'text-purple-600', bgColor: 'bg-purple-50' },
    { type: 'sms' as NotificationType, label: 'SMS', icon: Smartphone, color: 'text-orange-600', bgColor: 'bg-orange-50' }
  ];

  useEffect(() => {
    loadPreferences();
  }, []);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      console.log('Loading user preferences...');
      const prefs = await changePreferencesApi.getUserPreferences();
      console.log('Loaded preferences:', prefs);

      // Convert database preferences to frontend state
      const statePrefs: UserChangePreferenceState[] = prefs.map(pref => ({
        notification_type: pref.notification_type,
        entity_types: pref.entity_types || null,
        change_types: pref.change_types || null,
        severity_levels: pref.severity_levels || null,
        enabled: pref.enabled
      }));

      setPreferences(statePrefs);
    } catch (error) {
      console.error('Failed to load preferences:', error);
      notify.error('Failed to load notification preferences');
      // Set empty preferences on error so component can still function
      setPreferences([]);
    } finally {
      setLoading(false);
    }
  };

  const handlePreferenceChange = (
    notificationType: NotificationType,
    field: keyof UserChangePreferenceState,
    value: any
  ) => {
    setPreferences(prev => {
      const existingIndex = prev.findIndex(pref => pref.notification_type === notificationType);

      if (existingIndex >= 0) {
        // Update existing preference
        const updated = [...prev];
        updated[existingIndex] = { ...updated[existingIndex], [field]: value } as UserChangePreferenceState;
        return updated;
      } else {
        // Create new preference
        const newPref: UserChangePreferenceState = {
          notification_type: notificationType,
          entity_types: null,
          change_types: null,
          severity_levels: null,
          enabled: false,
          [field]: value
        } as UserChangePreferenceState;
        return [...prev, newPref];
      }
    });
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setSaveStatus('idle');
      setErrorMessage('');

      const updateData = {
        preferences: preferences.map(pref => ({
          notificationType: pref.notification_type,
          data: {
            entity_types: pref.entity_types || null,
            change_types: pref.change_types || null,
            severity_levels: pref.severity_levels || null,
            enabled: pref.enabled
          }
        }))
      };

      await changePreferencesApi.updateUserPreferences(updateData);
      setSaveStatus('success');
      notify.success('Notification preferences saved successfully');

      if (onSave) {
        onSave();
      }
    } catch (error) {
      console.error('Failed to save preferences:', error);
      setSaveStatus('error');
      setErrorMessage('Failed to save preferences');
      notify.error('Failed to save notification preferences');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = async () => {
    try {
      setResetting(true);
      const defaultPrefs = await changePreferencesApi.resetToDefaults();
      setPreferences(defaultPrefs);
      notify.success('Preferences reset to defaults');
    } catch (error) {
      console.error('Failed to reset preferences:', error);
      notify.error('Failed to reset preferences');
    } finally {
      setResetting(false);
    }
  };

  const getPreference = (notificationType: NotificationType): UserChangePreferenceState | undefined => {
    return preferences.find(p => p.notification_type === notificationType);
  };

  // Get preference with defaults for missing preferences
  const getPreferenceWithDefaults = (notificationType: NotificationType): UserChangePreferenceState => {
    const pref = getPreference(notificationType);
    return pref || {
      notification_type: notificationType,
      entity_types: null,
      change_types: null,
      severity_levels: null,
      enabled: false
    };
  };

  const toggleArrayItem = (array: string[] | null | undefined, item: string): string[] => {
    if (!array) return [item];
    return array.includes(item)
      ? array.filter(i => i !== item)
      : [...array, item];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-blue-600 border-t-transparent"></div>
        <span className="ml-3 text-slate-600">Loading preferences...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-slate-800">Change Notification Preferences</h3>
          <p className="text-sm text-slate-600">Customize which system changes you want to be notified about</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={handleReset}
            disabled={resetting}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`w-4 h-4 ${resetting ? 'animate-spin' : ''}`} />
            Reset to Defaults
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" />
            {saving ? 'Saving...' : 'Save Preferences'}
          </button>
        </div>
      </div>

      {/* Status Messages */}
      {saveStatus === 'success' && (
        <div className="flex items-center gap-2 p-4 bg-green-50 border border-green-200 rounded-lg">
          <CheckCircle className="w-5 h-5 text-green-600" />
          <span className="text-green-800">Preferences saved successfully!</span>
        </div>
      )}

      {saveStatus === 'error' && (
        <div className="flex items-center gap-2 p-4 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600" />
          <span className="text-red-800">{errorMessage || 'Failed to save preferences'}</span>
        </div>
      )}

      {/* Notification Channels */}
      {notificationChannels.map(channel => {
        const pref = getPreferenceWithDefaults(channel.type);
        const Icon = channel.icon;

        return (
          <div key={channel.type} className={`border rounded-lg p-6 ${channel.bgColor}`}>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <Icon className={`w-6 h-6 ${channel.color}`} />
                <div>
                  <h4 className="font-semibold text-slate-800">{channel.label} Notifications</h4>
                  <p className="text-sm text-slate-600">Configure {channel.label.toLowerCase()} notification preferences</p>
                </div>
              </div>
              <label className="relative inline-flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={pref?.enabled || false}
                  onChange={(e) => handlePreferenceChange(channel.type, 'enabled', e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-12 h-7 bg-slate-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            {pref?.enabled && (
              <div className="space-y-4">
                {/* Entity Types */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Entity Types</label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {entityTypes.map(entity => (
                      <label key={entity.value} className="flex items-center gap-2 p-2 border rounded hover:bg-white cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pref.entity_types?.includes(entity.value) || false}
                          onChange={() => handlePreferenceChange(
                            channel.type,
                            'entity_types',
                            toggleArrayItem(pref.entity_types, entity.value)
                          )}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <div>
                          <div className="text-sm font-medium">{entity.label}</div>
                          <div className="text-xs text-slate-500">{entity.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Change Types */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Change Types</label>
                  <div className="flex gap-4">
                    {changeTypes.map(change => (
                      <label key={change.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pref.change_types?.includes(change.value) || false}
                          onChange={() => handlePreferenceChange(
                            channel.type,
                            'change_types',
                            toggleArrayItem(pref.change_types, change.value)
                          )}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className="text-sm">{change.label}</span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Severity Levels */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Severity Levels</label>
                  <div className="flex gap-4">
                    {severityLevels.map(severity => (
                      <label key={severity.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={pref.severity_levels?.includes(severity.value) || false}
                          onChange={() => handlePreferenceChange(
                            channel.type,
                            'severity_levels',
                            toggleArrayItem(pref.severity_levels, severity.value)
                          )}
                          className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                        />
                        <span className={`text-sm font-medium ${severity.color}`}>{severity.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ChangeNotificationPreferences;